// src/pages/setup/SetupStep1.tsx - 採用 Welcome Page2 設計模式
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from 'lucide-react';
import { markPersonalizationStatus } from '../../services/auth';
import { logDev } from '../../utils/logger';
import { logEvent } from '../../utils/analytics';

const SetupStep1: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [answer, setAnswer] = useState('');

  // 處理 back navigation，恢復之前的答案
  useEffect(() => {
    const stateAnswer = location.state as { q1?: string } | null;
    if (stateAnswer?.q1) {
      setAnswer(stateAnswer.q1);
    }
  }, [location.state]);



  const handleNext = () => {
    if (answer.trim()) {
      logDev('SetupStep1', '用戶完成第一題', { answerLength: answer.length });

      // 追蹤：完成步驟 1
      try {
        logEvent('personalization_step_completed', { step: 1 });
      } catch (error) {
        // 靜默失敗
      }

      // 使用 navigation state 傳遞答案到下一步
      navigate('/setup/step-2', { state: { q1: answer.trim() } });
    } else {
      navigate('/setup/step-2');
    }
  };

  const handleSkip = () => {
    // 簡化：直接進入下一步，不需要複雜的狀態管理
    logDev('SetupStep1', '用戶跳過第一題，進入第二題');
    navigate('/setup/step-2');
  };

  const isValid = answer.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress Dots */}
      <div className="flex justify-center space-x-2 pt-4 pb-2">
        {Array.from({ length: 3 }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === 1;
          const isCompleted = stepNumber < 1;

          return (
            <div
              key={stepNumber}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                isActive
                  ? 'bg-gradient-to-r from-sky-500 to-sky-500 transform scale-125 animate-welcome-pulse-glow'
                  : isCompleted
                  ? 'bg-gradient-to-r from-sky-500 to-sky-500'
                  : 'bg-gray-300'
              }`}
            />
          );
        })}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-2">
        <div className="w-full max-w-sm mx-auto">
          <div className="space-y-6">
            {/* Header - 採用 Welcome Page2 的結構 */}
            <div className="text-center mt-4 animate-welcome-slide-up">
              {/* Icon - 獨立視覺焦點 */}
              <div className="mb-8">
                <div className="relative w-16 h-16 mx-auto bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                  {/* 光照效果 */}
                  <div className="absolute w-20 h-20 rounded-2xl border-2 border-sky-400 opacity-30 animate-welcome-pulse-glow"></div>
                </div>
              </div>

              {/* 標題 + 副標題群組 - 緊密文字說明 */}
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-gray-900 animate-welcome-slide-up delay-200">
                  What are your key roles?
                </h2>
                <p className="text-gray-600 px-2 animate-welcome-slide-up delay-400">
                  Life and work roles that define you
                </p>
              </div>
            </div>

            {/* Input Container - 採用 Welcome 的卡片化設計 */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200 animate-welcome-slide-up delay-600 opacity-0">
              {/* 輸入區域 */}
              <div className="relative mb-4">
                <textarea
                  placeholder="I'm a Product manager, parent, soccer coach.…"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-sky-400 focus:shadow-lg transition-all text-gray-700 placeholder-gray-400 bg-white"
                  rows={4}
                />
                <div className="absolute bottom-3 right-3 text-xs text-sky-400">
                  <span className="animate-typing-blink">|</span>
                </div>
              </div>

            </div>

            {/* Buttons - 使用統一 Apple 樣式 */}
            <div className="space-y-3">
              <button
                onClick={handleNext}
                disabled={!isValid}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg ${
                  isValid
                    ? 'btn-apple-primary'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isValid ? 'Continue →' : 'Share something about yourself'}
              </button>

              <button
                onClick={handleSkip}
                className="w-full btn-apple-secondary py-3 px-4 rounded-xl font-medium"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Safe Area */}
      <div className="pb-4"></div>
    </div>
  );
};

export default SetupStep1;